import { get, set, del, keys, values, createStore } from 'idb-keyval'
import { webmFixDuration } from 'webm-fix-duration'
import {
  expiredSessions,
  sessionsOverCap,
} from './interrupted_recording_helpers.js'

const store = createStore('simplero_voice_recordings', 'recordings')

export const InterruptedRecordingStore = {
  async beginSession({ sessionId, key, mimeType, startedAt }) {
    await set(
      sessionId,
      {
        sessionId,
        key,
        mimeType,
        startedAt,
        durationMs: 0,
        chunks: [],
        bytes: 0,
        shown: false,
      },
      store
    )
    navigator.storage?.persist?.() // best-effort; reduces eviction under pressure
  },

  // durationMs is recorded audio so far (excludes paused time) — used to label it.
  async appendChunks(sessionId, blobs, durationMs = null) {
    const record = await get(sessionId, store)
    if (!record) return
    record.chunks.push(...blobs)
    record.bytes += blobs.reduce((sum, b) => sum + b.size, 0)
    if (durationMs != null) record.durationMs = durationMs
    await set(sessionId, record, store)
  },

  // Stop offering this recording's restore toast (user restored or dismissed it).
  async markShown(sessionId) {
    const record = await get(sessionId, store)
    if (!record) return
    record.shown = true
    await set(sessionId, record, store)
  },

  // Best-effort partial update — used for resume flags / transcript cache.
  async updateSession(sessionId, attrs) {
    const record = await get(sessionId, store)
    if (!record) return
    Object.assign(record, attrs)
    await set(sessionId, record, store)
  },

  // Transcription was started (or is about to be). If the tab is killed mid-flight
  // (iOS lock), the composer re-opens and silently resumes from this flag.
  async markPendingResume(sessionId, { autoSubmit = false } = {}) {
    await this.updateSession(sessionId, {
      pendingResume: true,
      autoSubmit: !!autoSubmit,
      pendingResumeAt: Date.now(),
    })
  },

  async clearPendingResume(sessionId) {
    await this.updateSession(sessionId, {
      pendingResume: false,
      autoSubmit: false,
      pendingResumeAt: null,
    })
  },

  async deleteSession(sessionId) {
    await del(sessionId, store)
  },

  // The most recent recording for a key (regardless of shown state), or null.
  async latestForKey(key) {
    const matched = (await values(store)).filter((r) => r.key === key)
    return matched.sort((a, b) => b.startedAt - a.startedAt)[0] ?? null
  },

  async listAll() {
    return (await values(store)).sort((a, b) => b.startedAt - a.startedAt)
  },

  // Whether listAll() would return anything. Reads keys rather than values so a
  // page that only needs the count doesn't deserialize every recording's audio
  // chunks (up to the 200MB cap).
  async hasAny() {
    return (await keys(store)).length > 0
  },

  async setTranscript(sessionId, transcript) {
    const record = await get(sessionId, store)
    if (!record) return
    record.transcript = transcript
    await set(sessionId, record, store)
  },

  // Reassemble the playable blob from chunks. WebM from MediaRecorder has no duration
  // in its header, so the player can't decode/seek it — patch it in. mp4 needs no fix.
  async getBlob(sessionId) {
    const record = await get(sessionId, store)
    if (!record) return null

    const blob = new Blob(record.chunks, { type: record.mimeType })
    if (!record.mimeType.includes('webm')) return blob
    return webmFixDuration(blob, record.durationMs, record.mimeType)
  },

  async purgeExpired(now = Date.now()) {
    const expired = expiredSessions(await values(store), now)
    await Promise.all(expired.map((r) => this.deleteSession(r.sessionId)))
  },

  async enforceCap() {
    const evict = sessionsOverCap(await values(store))
    await Promise.all(evict.map((r) => this.deleteSession(r.sessionId)))
  },
}
